import { Injectable } from '@angular/core';
import { ProcedureEnum } from '@core/procedure-helpers/models/procedure.enum';
import { CaseTypeFormStateService } from '@modules/order/services/case-type-form-state.service';
import { OrderQuery } from '@modules/order/state/order-query';
import { positiveIntegersOnlyRegExp } from '@shared/models/consts';
import { IdName } from '@shared/models/id-name';
import { ProcedureMap } from '@shared/models/procedure-map';
import { isProcedureTypeHiddenByTemplate } from '@shared/rx-templates/rx-template-utils';
import { RxTemplateService } from '@shared/rx-templates/rx-template.service';
import { ProcedureFormStateService } from '@shared/services/procedure-form-state.service';
import { ShellQuery } from '@shared/store/shell/shell-query';
import type { Maybe } from '@/types/globals';

@Injectable({ providedIn: 'root' })
export class OrderValidationService {
	private static get requiredTypeProcedureIds() {
		return [ProcedureEnum.Appliance, ProcedureEnum.ImplantPlanning, ProcedureEnum.Denture_Removable, ProcedureEnum.Invisalign];
	}

	constructor(
		private readonly orderQuery: OrderQuery,
		private readonly shellQuery: ShellQuery,
		private readonly rxTemplateService: RxTemplateService,
		private readonly procedureFormStateService: ProcedureFormStateService,
		private readonly caseTypeFormStateService: CaseTypeFormStateService
	) {}

	isProcedureFlowOrderValid(procedureMap: ProcedureMap): boolean {
		const requiresType = OrderValidationService.requiredTypeProcedureIds.includes(procedureMap.ProcedureId);

		return (
			!requiresType ||
			procedureMap.TypeId > 0 ||
			isProcedureTypeHiddenByTemplate(procedureMap.ProcedureId, this.rxTemplateService.currentTemplate)
		);
	}

	isCaseTypeFlowOrderValid(caseType: Maybe<IdName>, currentAlignerId: Maybe<string>): boolean {
		if (!caseType?.Id) {
			return false;
		}

		if (!this.caseTypeFormStateService.isCurrentAlignerRequired({ caseType })) {
			return true;
		}

		return !currentAlignerId || (!!positiveIntegersOnlyRegExp.exec(currentAlignerId) && +currentAlignerId <= 99);
	}

	isOrderValid(caseType: Maybe<IdName>, currentAlignerId: Maybe<string>, procedureMap: Maybe<ProcedureMap>): boolean {
		return (
			this.isCaseTypeFlowOrderValid(caseType, currentAlignerId) ||
			(!!procedureMap?.Id && this.isProcedureFlowOrderValid(procedureMap))
		);
	}

	isOrderSectionValidForSend(): boolean {
		return this.shellQuery.isProcedureFlow
			? this.isProcedureFlowOrderSectionValidForSend()
			: this.isCaseTypeFlowOrderSectionValidForSend();
	}

	private isProcedureFlowOrderSectionValidForSend(): boolean {
		const template = this.rxTemplateService.currentTemplate;
		const { sendTo, dueDate } = this.procedureFormStateService.getFormState(
			this.orderQuery.procedureMap,
			this.shellQuery.isReadOnly,
			this.orderQuery.treatmentStage,
			true, // dont change it to shellQuery.shouldValidateForSend!
			// Because it wrongly marks control "valid to send" during saving rx!!!
			this.shellQuery.procedureMaps,
			template
		);
		const isSendToValid = !sendTo.isRequired || !!this.orderQuery.sendTo;
		const isDueDateValid = !dueDate.isRequired || !!this.orderQuery.dueDate;

		return isSendToValid && isDueDateValid;
	}

	private isCaseTypeFlowOrderSectionValidForSend(): boolean {
		const state = this.orderQuery.getValue();
		const isSendToRequired = this.caseTypeFormStateService.isSendToRequired({ caseType: state.caseType });
		const isDueDateRequired = this.caseTypeFormStateService.isDueDateRequired({ caseType: state.caseType });

		return (!isSendToRequired || !!state.sendTo) && (!isDueDateRequired || !!state.dueDate);
	}
}
